import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Badge, 
  Menu, 
  MenuItem, 
  Typography, 
  Box, 
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { httpGet, httpPut } from '../lib/http';

const getTypeIcon = (type) => {
  switch (type) {
    case 'RECRUITMENT': return <BusinessCenterIcon color="primary" />;
    case 'TEST_RESULTS': return <AssessmentIcon color="secondary" />;
    case 'SYSTEM': return <SettingsIcon />;
    default: return <NotificationsIcon />;
  }
};

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const open = Boolean(anchorEl);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const resp = await httpGet('/api/notify/inbox');
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.notifications.filter(n => !n.read).length);
      }
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // In a real production app, we would use a Firestore onSnapshot listener here
    // for true real-time updates. For now, we fetch on mount.
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async (id) => {
    try {
      await httpPut(`/api/notify/read/${id}`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark read', err);
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon sx={{ color: 'text.primary' }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { width: 360, maxHeight: 480, mt: 1.5, overflow: 'hidden' }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unreadCount > 0 && <Typography variant="caption" color="primary">{unreadCount} unread</Typography>}
        </Box>
        <Divider />

        <List sx={{ p: 0, overflowY: 'auto', maxHeight: 380 }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
            </Box>
          ) : (
            notifications.map((n) => (
              <ListItem 
                key={n.id} 
                button 
                onClick={() => markAsRead(n.id)}
                sx={{ 
                  bgcolor: n.read ? 'transparent' : 'rgba(255, 215, 0, 0.15)',
                  borderLeft: n.read ? 'none' : '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{getTypeIcon(n.type)}</ListItemIcon>
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.primary" sx={{ my: 0.5 }}>{n.body}</Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(n.createdAt).toLocaleDateString()}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: n.read ? 500 : 700 }}
                />
              </ListItem>
            ))
          )}
        </List>
        <Divider />
        <MenuItem sx={{ justifyContent: 'center', py: 1.5 }} onClick={handleClose}>
          <Typography variant="body2" color="primary" fontWeight={600}>View All</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
